import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function AccountPage() {
    const { user, loading } = useAuth();
    const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
    const [saving, setSaving] = useState(false);
    const [showForm, setShowForm] = useState(false);

    const changePw = async (e) => {
        e.preventDefault();
        if (pw.new_password !== pw.confirm) { toast.error("Las contraseñas nuevas no coinciden"); return; }
        setSaving(true);
        try {
            await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
            toast.success("Contraseña actualizada");
            setPw({ current_password: "", new_password: "", confirm: "" });
            setShowForm(false);
        } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
    };
    const { data: orders = [] } = useQuery({ queryKey: ["my-orders"], queryFn: async () => (await api.get("/my-orders")).data, enabled: !!user });
    if (loading) return <div className="py-24 text-center">Cargando…</div>;
    if (!user) return <Navigate to="/login" replace />;
    return (
        <div className="mx-auto max-w-4xl px-4 sm:px-8 py-10">
            <p className="text-eyebrow">Hola, {user.name}</p>
            <h1 className="font-display text-4xl mt-2 mb-2">Mi cuenta</h1>
            <p className="text-sm text-ink/60 mb-10">{user.email} · {user.role === "admin" ? "Administrador" : "Cliente"}</p>
            {user.role === "admin" && <Link to="/admin" className="btn-ink inline-block mb-8 mr-2">Ir al Panel Admin</Link>}
            <button onClick={() => setShowForm((v) => !v)} data-testid="btn-toggle-change-pw" className="btn-outline inline-flex items-center gap-2 mb-8"><KeyRound className="w-4 h-4" />Cambiar contraseña</button>
            {showForm && (
                <form onSubmit={changePw} className="bg-white border border-sandLight rounded-lg p-6 mb-10 max-w-md space-y-3">
                    <p className="text-eyebrow mb-2">Cambiar contraseña</p>
                    <input required type="password" placeholder="Contraseña actual" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="w-full border border-sandLight rounded-md px-3 py-2 text-sm" data-testid="pw-current" />
                    <input required type="password" minLength={6} placeholder="Nueva contraseña (min 6)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="w-full border border-sandLight rounded-md px-3 py-2 text-sm" data-testid="pw-new" />
                    <input required type="password" placeholder="Confirmar nueva contraseña" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="w-full border border-sandLight rounded-md px-3 py-2 text-sm" data-testid="pw-confirm" />
                    <button disabled={saving} data-testid="btn-save-password" className="btn-ink w-full">{saving ? "Guardando…" : "Actualizar contraseña"}</button>
                </form>
            )}
            <h2 className="font-display text-2xl mb-4">Mis pedidos</h2>
            {orders.length === 0 ? <p className="text-sm text-ink/60">Aún no tienes pedidos.</p> : (
                <div className="divide-y divide-sandLight border-y border-sandLight">
                    {orders.map((o) => (
                        <Link key={o.id} to={`/pedido/${o.id}`} className="py-4 flex items-center justify-between hover:bg-white/60 px-2">
                            <div>
                                <p className="text-sm">#{o.id.slice(0, 8)}</p>
                                <p className="text-xs text-ink/60">{new Date(o.created_at).toLocaleString("es-CO")}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-sm">{formatPrice(o.total)}</p>
                                <p className="text-xs uppercase tracking-wide text-ink/60">{o.status}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
